import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Bug,
  CircleCheckBig,
  Clock3,
  Headset,
  KeyRound,
  MessageSquareText,
  ReceiptText,
  Send,
  Sparkles,
  UserRound,
} from 'lucide-react';

import { submitSupportRequest } from '../../api/dataService';
import { useAuth } from '../../context/AuthContext';
import {
  createInitialSupportFormState,
  getCategoryOptionLabel,
  getSupportFieldsForCategory,
  SUPPORT_ACCOUNT_ACCESS_ISSUE_OPTIONS,
  SUPPORT_BILLING_ISSUE_OPTIONS,
  SUPPORT_BILLING_PLATFORM_OPTIONS,
  SUPPORT_CATEGORIES,
  SUPPORT_CATEGORY_OPTIONS,
  SUPPORT_FIELD_LABELS,
  SUPPORT_PLATFORM_OPTIONS,
  SUPPORT_SEVERITY_OPTIONS,
} from '../../utils/supportFormConfig';

const cardClassName = 'rounded-[1.75rem] border border-white/10 bg-slate-900/70 shadow-[0_24px_80px_rgba(2,8,23,0.42)] backdrop-blur-xl';
const inputClassName = 'w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30';
const textareaClassName = `${inputClassName} min-h-[120px] resize-y`;

const CATEGORY_META = {
  [SUPPORT_CATEGORIES.BUG_REPORT]: {
    icon: Bug,
    title: 'Bug report',
    description: 'Capture reproducible issues clearly so they can be fixed faster.',
    accent: 'text-rose-300',
    surface: 'from-rose-500/20 via-rose-500/5 to-transparent',
  },
  [SUPPORT_CATEGORIES.TECHNICAL_SUPPORT]: {
    icon: Headset,
    title: 'Technical support',
    description: 'Get help with a feature or flow that is not working as expected.',
    accent: 'text-cyan-300',
    surface: 'from-cyan-500/20 via-cyan-500/5 to-transparent',
  },
  [SUPPORT_CATEGORIES.ACCOUNT_ACCESS]: {
    icon: KeyRound,
    title: 'Account access',
    description: 'Use this for login, verification, subscription access, or missing data problems.',
    accent: 'text-amber-300',
    surface: 'from-amber-500/20 via-amber-500/5 to-transparent',
  },
  [SUPPORT_CATEGORIES.BILLING_SUBSCRIPTION]: {
    icon: ReceiptText,
    title: 'Billing/subscription',
    description: 'Report purchase, restore, or plan-state issues with precise store context.',
    accent: 'text-emerald-300',
    surface: 'from-emerald-500/20 via-emerald-500/5 to-transparent',
  },
  [SUPPORT_CATEGORIES.FEATURE_REQUEST]: {
    icon: Sparkles,
    title: 'Feature request',
    description: 'Describe the gap and the outcome you want, not just the UI idea.',
    accent: 'text-fuchsia-300',
    surface: 'from-fuchsia-500/20 via-fuchsia-500/5 to-transparent',
  },
  [SUPPORT_CATEGORIES.GENERAL_QUESTION]: {
    icon: MessageSquareText,
    title: 'General question',
    description: 'Use this when the request does not fit a more specific operational bucket.',
    accent: 'text-slate-200',
    surface: 'from-slate-400/15 via-slate-400/5 to-transparent',
  },
};

const getVisibleFieldPlaceholder = (fieldKey) => {
  switch (fieldKey) {
    case 'subject':
      return 'Short summary of the request';
    case 'message':
      return 'Add any extra context that support should know.';
    case 'featureArea':
      return 'Examples: onboarding, quest timer, payments';
    case 'whatHappened':
      return 'Describe the issue and any blocked action.';
    case 'stepsToReproduce':
      return 'List the steps that reliably trigger the bug.';
    case 'expectedBehavior':
      return 'What should have happened instead?';
    case 'actualBehavior':
      return 'What happened in practice?';
    case 'desiredOutcome':
      return 'What change would solve this for you?';
    default:
      return '';
  }
};

const SupportSelectField = ({ id, label, value, onChange, options }) => {
  return (
    <label className="block">
      <span className="mb-2 block text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">
        {label}
      </span>
      <select
        id={id}
        value={value}
        onChange={onChange}
        className={inputClassName}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
};

const SupportTextField = ({ id, label, value, onChange, placeholder, readOnly = false }) => {
  return (
    <label className="block">
      <span className="mb-2 block text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">
        {label}
      </span>
      <input
        id={id}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`${inputClassName} ${readOnly ? 'cursor-not-allowed border-white/5 bg-slate-900/60 text-slate-300' : ''}`}
      />
    </label>
  );
};

const SupportTextAreaField = ({ id, label, value, onChange, placeholder }) => {
  return (
    <label className="block">
      <span className="mb-2 block text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">
        {label}
      </span>
      <textarea
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={textareaClassName}
      />
    </label>
  );
};

const Support = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [formState, setFormState] = useState(() => createInitialSupportFormState());
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const visibleFields = useMemo(() => getSupportFieldsForCategory(formState.category), [formState.category]);
  const categoryMeta = CATEGORY_META[formState.category] || CATEGORY_META[SUPPORT_CATEGORIES.GENERAL_QUESTION];
  const replyEmail = user?.email || '';

  useEffect(() => {
    if (formState.category === SUPPORT_CATEGORIES.BILLING_SUBSCRIPTION) {
      setFormState((prev) => (
        prev.platform === 'ios_app' ? { ...prev, platform: 'web' } : prev
      ));
      return;
    }

    setFormState((prev) => (
      prev.platform === 'ios_app_store' ? { ...prev, platform: 'web' } : prev
    ));
  }, [formState.category]);

  useEffect(() => {
    if (visibleFields.includes('screenshot') || !formState.screenshot) {
      return;
    }
    setFormState((prev) => ({ ...prev, screenshot: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [formState.screenshot, visibleFields]);

  const updateField = (field) => (event) => {
    const nextValue = event?.target?.value ?? '';
    setFormState((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleFileChange = (event) => {
    const nextFile = event?.target?.files?.[0] || null;
    setFormState((prev) => ({ ...prev, screenshot: nextFile }));
  };

  const resetForm = () => {
    setFormState(createInitialSupportFormState());
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!replyEmail) {
      setSuccessMessage('');
      setErrorMessage('Account email is missing. Refresh your session and try again.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    const payload = new FormData();
    payload.append('category', formState.category);
    payload.append('subject', formState.subject);
    payload.append('message', formState.message);

    visibleFields.forEach((fieldKey) => {
      if (fieldKey === 'screenshot') {
        if (formState.screenshot) {
          payload.append('screenshot', formState.screenshot);
        }
        return;
      }

      const value = formState[fieldKey];
      if (typeof value === 'string') {
        payload.append(fieldKey, value);
      }
    });

    try {
      await submitSupportRequest(payload);
      resetForm();
      setSuccessMessage('Your request has been delivered to Forza support.');
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || 'Unable to send your request right now. Please try again later.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const Icon = categoryMeta.icon;
  const categoryLabel = getCategoryOptionLabel(formState.category);
  const selectedPlatformOptions = formState.category === SUPPORT_CATEGORIES.BILLING_SUBSCRIPTION
    ? SUPPORT_BILLING_PLATFORM_OPTIONS
    : SUPPORT_PLATFORM_OPTIONS;

  return (
    <div className="min-h-screen bg-[#0f1115] text-slate-100">
      <div className="mx-auto max-w-6xl px-4 pb-36 pt-6 sm:px-6 lg:px-8">
        <header className={`${cardClassName} relative overflow-hidden px-6 py-7 sm:px-8`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.16),transparent_35%),radial-gradient(circle_at_80%_20%,rgba(56,189,248,0.12),transparent_28%)]" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-cyan-200">
                <Headset className="h-3.5 w-3.5" />
                Support
              </div>
              <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">Reach Forza support.</h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 sm:text-base">
                Choose the right category, add concise context, and the request will be sent directly to the support inbox used for operational follow-up.
              </p>
            </div>

            <div className="grid gap-3 lg:min-w-[360px]">
              <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                <div className="flex items-center gap-2 text-cyan-200">
                  <Clock3 className="h-4 w-4" />
                  <span className="text-xs font-black uppercase tracking-[0.22em]">Response target</span>
                </div>
                <p className="mt-3 text-lg font-bold text-white">24 to 48 hours</p>
                <p className="mt-1 text-sm text-slate-400">Operational issues usually get a first response within two business days.</p>
              </div>
            </div>
          </div>
        </header>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(320px,0.9fr)]">
          <div className="space-y-6">
            {successMessage ? (
              <section className={`${cardClassName} border-emerald-400/20 bg-emerald-500/10 px-6 py-5`} aria-live="polite">
                <div className="flex items-start gap-3">
                  <CircleCheckBig className="mt-0.5 h-5 w-5 text-emerald-300" />
                  <div>
                    <h2 className="text-lg font-bold text-white">Message sent</h2>
                    <p className="mt-1 text-sm text-emerald-100/90">{successMessage}</p>
                  </div>
                </div>
              </section>
            ) : null}

            {errorMessage ? (
              <section className={`${cardClassName} border-rose-400/20 bg-rose-500/10 px-6 py-5`} aria-live="polite">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 text-rose-200" />
                  <div>
                    <h2 className="text-lg font-bold text-white">Unable to send request</h2>
                    <p className="mt-1 text-sm text-rose-100/90">{errorMessage}</p>
                  </div>
                </div>
              </section>
            ) : null}

            <section className={`${cardClassName} overflow-hidden`}>
              <div className={`bg-gradient-to-r ${categoryMeta.surface} px-6 py-5`}>
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                    <Icon className={`h-5 w-5 ${categoryMeta.accent}`} />
                  </div>
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Current intake mode</p>
                    <h2 className="mt-1 text-xl font-bold text-white">{categoryMeta.title}</h2>
                    <p className="mt-1 text-sm text-slate-300">{categoryMeta.description}</p>
                  </div>
                </div>
              </div>

              <form className="px-6 py-6" onSubmit={handleSubmit}>
                <div className="grid gap-5 md:grid-cols-2">
                  <SupportSelectField
                    id="support-category"
                    label="Category"
                    value={formState.category}
                    onChange={updateField('category')}
                    options={SUPPORT_CATEGORY_OPTIONS}
                  />
                  <SupportTextField
                    id="support-reply-email"
                    label="Reply email"
                    value={replyEmail}
                    readOnly
                  />
                </div>

                <div className="mt-5 grid gap-5">
                  <SupportTextField
                    id="support-subject"
                    label="Subject"
                    value={formState.subject}
                    onChange={updateField('subject')}
                    placeholder={getVisibleFieldPlaceholder('subject')}
                  />

                  {visibleFields.includes('platform') ? (
                    <SupportSelectField
                      id="support-platform"
                      label="Platform"
                      value={formState.platform}
                      onChange={updateField('platform')}
                      options={selectedPlatformOptions}
                    />
                  ) : null}

                  {visibleFields.includes('severity') ? (
                    <SupportSelectField
                      id="support-severity"
                      label="Severity"
                      value={formState.severity}
                      onChange={updateField('severity')}
                      options={SUPPORT_SEVERITY_OPTIONS}
                    />
                  ) : null}

                  {visibleFields.includes('issueType') ? (
                    <SupportSelectField
                      id="support-issue-type"
                      label="Issue type"
                      value={formState.issueType}
                      onChange={updateField('issueType')}
                      options={
                        formState.category === SUPPORT_CATEGORIES.BILLING_SUBSCRIPTION
                          ? SUPPORT_BILLING_ISSUE_OPTIONS
                          : SUPPORT_ACCOUNT_ACCESS_ISSUE_OPTIONS
                      }
                    />
                  ) : null}

                  {visibleFields.includes('featureArea') ? (
                    <SupportTextField
                      id="support-feature-area"
                      label={SUPPORT_FIELD_LABELS.featureArea}
                      value={formState.featureArea}
                      onChange={updateField('featureArea')}
                      placeholder={getVisibleFieldPlaceholder('featureArea')}
                    />
                  ) : null}

                  {visibleFields.includes('whatHappened') ? (
                    <SupportTextAreaField
                      id="support-what-happened"
                      label={SUPPORT_FIELD_LABELS.whatHappened}
                      value={formState.whatHappened}
                      onChange={updateField('whatHappened')}
                      placeholder={getVisibleFieldPlaceholder('whatHappened')}
                    />
                  ) : null}

                  {visibleFields.includes('stepsToReproduce') ? (
                    <SupportTextAreaField
                      id="support-steps"
                      label={SUPPORT_FIELD_LABELS.stepsToReproduce}
                      value={formState.stepsToReproduce}
                      onChange={updateField('stepsToReproduce')}
                      placeholder={getVisibleFieldPlaceholder('stepsToReproduce')}
                    />
                  ) : null}

                  {visibleFields.includes('expectedBehavior') ? (
                    <SupportTextAreaField
                      id="support-expected"
                      label={SUPPORT_FIELD_LABELS.expectedBehavior}
                      value={formState.expectedBehavior}
                      onChange={updateField('expectedBehavior')}
                      placeholder={getVisibleFieldPlaceholder('expectedBehavior')}
                    />
                  ) : null}

                  {visibleFields.includes('actualBehavior') ? (
                    <SupportTextAreaField
                      id="support-actual"
                      label={SUPPORT_FIELD_LABELS.actualBehavior}
                      value={formState.actualBehavior}
                      onChange={updateField('actualBehavior')}
                      placeholder={getVisibleFieldPlaceholder('actualBehavior')}
                    />
                  ) : null}

                  {visibleFields.includes('desiredOutcome') ? (
                    <SupportTextAreaField
                      id="support-outcome"
                      label={SUPPORT_FIELD_LABELS.desiredOutcome}
                      value={formState.desiredOutcome}
                      onChange={updateField('desiredOutcome')}
                      placeholder={getVisibleFieldPlaceholder('desiredOutcome')}
                    />
                  ) : null}

                  {visibleFields.includes('screenshot') ? (
                    <label className="block">
                      <span className="mb-2 block text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">
                        Screenshot
                      </span>
                      <div className="rounded-2xl border border-dashed border-white/15 bg-slate-950/60 p-4">
                        <input
                          ref={fileInputRef}
                          id="support-screenshot"
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleFileChange}
                          className="block w-full text-sm text-slate-300 file:mr-4 file:rounded-xl file:border-0 file:bg-cyan-400/15 file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-cyan-100 hover:file:bg-cyan-400/25"
                        />
                        <p className="mt-2 text-xs text-slate-500">Optional. PNG, JPEG, or WebP up to 5 MB.</p>
                      </div>
                    </label>
                  ) : null}

                  <SupportTextAreaField
                    id="support-message"
                    label="Message"
                    value={formState.message}
                    onChange={updateField('message')}
                    placeholder={getVisibleFieldPlaceholder('message')}
                  />
                </div>

                <div className="mt-6 flex flex-col gap-3 border-t border-white/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-slate-400">
                    Selected path: <span className="font-semibold text-slate-200">{categoryLabel}</span>
                  </p>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold transition ${
                      isSubmitting
                        ? 'cursor-not-allowed bg-slate-700 text-slate-300'
                        : 'bg-cyan-300 text-slate-950 hover:bg-cyan-200'
                    }`}
                  >
                    <Send className="h-4 w-4" />
                    {isSubmitting ? 'Sending request...' : 'Send request'}
                  </button>
                </div>
              </form>
            </section>
          </div>

          <aside className="space-y-6">
            <section className={`${cardClassName} px-5 py-5`}>
              <div className="flex items-center gap-3">
                <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-3">
                  <UserRound className="h-5 w-5 text-cyan-300" />
                </div>
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Account context</p>
                  <h2 className="mt-1 text-lg font-bold text-white">Replies are bound to your account email.</h2>
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/70 p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Signed-in email</p>
                <p className="mt-2 break-all text-sm font-semibold text-slate-100">{replyEmail || 'No account email available'}</p>
                <p className="mt-3 text-xs leading-5 text-slate-400">
                  Support uses this address as the reply target. If the account email is wrong, fix the account first before sending the request.
                </p>
              </div>
            </section>

            <section className={`${cardClassName} px-5 py-5`}>
              <div className="flex items-center gap-3">
                <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-3">
                  <AlertCircle className="h-5 w-5 text-cyan-300" />
                </div>
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-400">Best results</p>
                  <h2 className="mt-1 text-lg font-bold text-white">Keep the signal high.</h2>
                </div>
              </div>

              <ul className="mt-4 space-y-3 text-sm text-slate-300">
                <li className="rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3">Use a short subject with one issue per request.</li>
                <li className="rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3">For bugs, describe exact repro steps instead of only saying it is broken.</li>
                <li className="rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3">For feature requests, explain the outcome you need, not only the preferred UI.</li>
              </ul>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Support;
